import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Star, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface ToolExpertsSectionProps {
  libraryItemId: string;
  libraryItemTitle: string;
}

interface ToolExpert {
  id: string;
  display_name: string | null;
  proficiency: string;
}

const PROFICIENCY_LEVELS = [
  { value: "beginner", label: "Beginner", stars: 1 },
  { value: "intermediate", label: "Intermediate", stars: 2 },
  { value: "expert", label: "Expert", stars: 3 },
];

const ToolExpertsSection: React.FC<ToolExpertsSectionProps> = ({ libraryItemId, libraryItemTitle }) => {
  const { data: experts, isLoading } = useQuery({
    queryKey: ["tool-experts", libraryItemId],
    queryFn: async () => {
      // First, find tool_tag linked to this library item
      const { data: toolTag } = await supabase
        .from("tool_tags")
        .select("id")
        .eq("library_resource_id", libraryItemId)
        .maybeSingle();
      
      if (!toolTag) {
        // Try to find by name match
        const { data: toolByName } = await supabase
          .from("tool_tags")
          .select("id")
          .ilike("name", libraryItemTitle)
          .maybeSingle();
        
        if (!toolByName) return [];
        
        // Fetch contributors for this tool who are open to networking
        const { data: contributors } = await supabase
          .from("contributor_tools")
          .select(`
            id,
            proficiency,
            profile_id,
            contributor_profiles!inner (
              id,
              display_name,
              open_to_networking
            )
          `)
          .eq("tool_tag_id", toolByName.id);
        
        if (!contributors) return [];
        
        return contributors
          .filter((c: any) => c.contributor_profiles?.open_to_networking)
          .map((c: any) => ({
            id: c.profile_id,
            display_name: c.contributor_profiles?.display_name,
            proficiency: c.proficiency || "beginner",
          })) as ToolExpert[];
      }
      
      // Fetch contributors for this tool who are open to networking
      const { data: contributors } = await supabase
        .from("contributor_tools")
        .select(`
          id,
          proficiency,
          profile_id,
          contributor_profiles!inner (
            id,
            display_name,
            open_to_networking
          )
        `)
        .eq("tool_tag_id", toolTag.id);
      
      if (!contributors) return [];
      
      return contributors
        .filter((c: any) => c.contributor_profiles?.open_to_networking)
        .map((c: any) => ({
          id: c.profile_id,
          display_name: c.contributor_profiles?.display_name,
          proficiency: c.proficiency || "beginner",
        })) as ToolExpert[];
    },
    enabled: !!libraryItemId && !!libraryItemTitle,
  });

  const renderProficiencyStars = (level: string) => {
    const levelConfig = PROFICIENCY_LEVELS.find(l => l.value === level);
    if (!levelConfig) return null;
    
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3].map(i => (
          <Star
            key={i}
            className={cn(
              "h-2.5 w-2.5",
              i <= levelConfig.stars 
                ? "fill-primary text-primary" 
                : "text-muted-foreground/30"
            )}
          />
        ))}
      </div>
    );
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getProficiencyLabel = (level: string) => {
    return PROFICIENCY_LEVELS.find(l => l.value === level)?.label || level;
  };

  if (isLoading) {
    return null; // Don't show loading state, just hide if loading
  }

  // Sort by proficiency (expert first)
  const sortedExperts = [...(experts || [])].sort((a, b) => {
    const order = { expert: 0, intermediate: 1, beginner: 2 };
    return (order[a.proficiency as keyof typeof order] || 2) - (order[b.proficiency as keyof typeof order] || 2);
  });

  const displayedExperts = sortedExperts.slice(0, 8);
  const remainingCount = sortedExperts.length - 8;

  if (sortedExperts.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <UserPlus className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium text-foreground">Know this tool?</p>
              <p className="text-sm">
                <Link to="/dashboard/profile" className="text-primary hover:underline">
                  Add it to your profile
                </Link>
                {" "}to be listed as an expert.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" />
          People Who Know This Tool
        </CardTitle>
        <CardDescription>
          {sortedExperts.length} {sortedExperts.length === 1 ? "person" : "people"} in our community {sortedExperts.length === 1 ? "uses" : "use"} this tool
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3">
          {displayedExperts.map(expert => (
            <div
              key={expert.id}
              className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getInitials(expert.display_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium leading-tight">
                  {expert.display_name || "Anonymous"}
                </span>
                <div className="flex items-center gap-1.5">
                  {renderProficiencyStars(expert.proficiency)}
                  <span className="text-xs text-muted-foreground">
                    {getProficiencyLabel(expert.proficiency)}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {remainingCount > 0 && (
            <div className="flex items-center justify-center px-4 py-2 bg-muted/30 rounded-lg border border-dashed border-border">
              <span className="text-sm text-muted-foreground">
                +{remainingCount} more
              </span>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Need help with this tool? Reach out to one of these community members.
        </p>
      </CardContent>
    </Card>
  );
};

export default ToolExpertsSection;
